import type { RequestFn } from '../http';

/** Teamkalender (Backend: routes/calendar.ts, Regeln: services/calendar/calendarRules.ts). */

export type CalendarCategory = 'JOUR_FIXE' | 'PROJECT' | 'CUSTOMER' | 'INTERNAL' | 'TRAINING' | 'OTHER';
export type CalendarAudience = 'PEOPLE' | 'TEAM' | 'TECHNICIANS' | 'LEADERSHIP';
export type CalendarResponse = 'PENDING' | 'ACCEPTED' | 'DECLINED';
export type CalendarScope = 'SERIES' | 'OCCURRENCE';

export type CalendarRecurrence =
  | { freq: 'WEEKLY'; interval: number; weekdays: number[] }
  | { freq: 'MONTHLY'; interval: number; by: 'DAY' }
  | { freq: 'MONTHLY'; interval: number; by: 'NTH_WEEKDAY'; nth: 1 | 2 | 3 | 4 | -1 };

export interface CalendarRecurrenceInput {
  freq: 'WEEKLY' | 'MONTHLY';
  interval?: number;
  weekdays?: number[];
  by?: 'DAY' | 'NTH_WEEKDAY';
  nth?: number;
}

/** Berliner Datum + Uhrzeit — so reisen Zeiten zum Server (nie ISO aus der Browser-Uhr). */
export interface CalendarWhen {
  /** 'JJJJ-MM-TT' */
  date: string;
  /** 'HH:mm', bei ganztägig null */
  time: string | null;
}

export interface CalendarPerson {
  id: string;
  displayName: string;
  role: string;
  /** Personenfarbe (lib/personColors) — null nur bei alten Antworten */
  color?: string | null;
}

export interface CalendarAttendee {
  userId: string;
  displayName: string;
  required: boolean;
  source: 'INVITED' | 'AUDIENCE';
  response: CalendarResponse | null;
  responseNote: string | null;
  respondedAt: string | null;
  seen: boolean | null;
}

export interface CalendarEvent {
  id: string;
  /** Privat und nicht dabei: nur „Belegt" */
  restricted: boolean;
  title: string;
  description: string | null;
  location: string | null;
  onlineUrl: string | null;
  category: CalendarCategory;
  isPrivate: boolean;
  allDay: boolean;
  startsAt: string;
  endsAt: string;
  start: CalendarWhen;
  /** Ganztägig: letzter Tag einschließlich */
  end: CalendarWhen;
  recurrence: CalendarRecurrence | null;
  recurrenceUntil: string | null;
  recurrenceLabel: string | null;
  audience: CalendarAudience;
  audienceRequired: boolean;
  organizer: { id: string; displayName: string } | null;
  customer: { id: string; name: string } | null;
  project: { id: string; number: string; title: string } | null;
  ticket: { id: string; number: string | null; subject: string | null } | null;
  reminderMinutes: number | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  attendees: CalendarAttendee[];
  me: { required: boolean; response: CalendarResponse; responseNote: string | null } | null;
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarOccurrence {
  /** `${eventId}:${occurrenceDate}` */
  key: string;
  eventId: string;
  occurrenceDate: string;
  start: string;
  end: string;
  /** 'JJJJ-MM-TT HH:mm:ss' Berliner Wanduhr */
  startLocal: string;
  endLocal: string;
  allDay: boolean;
  moved: boolean;
  cancelled: boolean;
  cancelReason: string | null;
  isSeries: boolean;
}

export interface CalendarCustomerAppointment {
  id: string;
  userId: string | null;
  technicianName: string;
  customer: { id: string; name: string };
  title: string;
  status: string;
  start: string;
  end: string;
  startLocal: string;
  endLocal: string;
  location: string | null;
}

export interface CalendarOnCallShift {
  id: string;
  userId: string | null;
  backupUserId: string | null;
  status: string;
  start: string;
  end: string;
  startLocal: string;
  endLocal: string;
}

export interface CalendarAbsence {
  id: string;
  userId: string;
  startsOn: string;
  endsOn: string;
  label: string;
}

export interface CalendarProjectDate {
  key: string;
  projectId: string;
  number: string;
  title: string;
  kind: 'START' | 'GO_LIVE' | 'END' | 'MILESTONE';
  label: string;
  date: string;
  leadUserId: string | null;
  done: boolean;
}

export interface CalendarTodoDue {
  id: string;
  title: string;
  date: string;
  time: string | null;
}

/** Termin aus Outlook — nur zum Anzeigen. Den Betreff sieht nur der Besitzer, alle anderen „Belegt". */
export interface CalendarOutlookBusy {
  id: string;
  userId: string;
  mine: boolean;
  /** Betreff und Ort sichtbar: eigener Termin, oder Admin bei nicht privaten. Sonst „Belegt". */
  detailed?: boolean;
  title: string;
  location: string | null;
  isPrivate: boolean;
  allDay: boolean;
  /** Nur mit Details */
  showAs: string | null;
  startLocal: string;
  endLocal: string;
}

export interface CalendarLayers {
  customerAppointments: CalendarCustomerAppointment[];
  onCall: CalendarOnCallShift[];
  absences: CalendarAbsence[];
  projects: CalendarProjectDate[];
  todos: CalendarTodoDue[];
  /** Fehlt bei älterem Server-Stand */
  outlook?: CalendarOutlookBusy[];
}

export interface CalendarFeed {
  from: string;
  to: string;
  people: CalendarPerson[];
  events: CalendarEvent[];
  occurrences: CalendarOccurrence[];
  layers: CalendarLayers;
}

export interface CalendarUpcoming {
  events: CalendarEvent[];
  occurrences: CalendarOccurrence[];
  appointments: CalendarCustomerAppointment[];
}

export interface CalendarEventInput {
  title: string;
  description?: string | null;
  location?: string | null;
  onlineUrl?: string | null;
  category: CalendarCategory;
  isPrivate?: boolean;
  allDay: boolean;
  start: CalendarWhen;
  end: CalendarWhen;
  recurrence?: CalendarRecurrenceInput | null;
  recurrenceUntil?: string | null;
  audience: CalendarAudience;
  audienceRequired: boolean;
  attendees: Array<{ userId: string; required: boolean }>;
  customerId?: string | null;
  projectId?: string | null;
  ticket?: { id: string; number?: string | null; subject?: string | null } | null;
  reminderMinutes?: number | null;
}

export interface CalendarBusyItem {
  kind: 'ABSENCE' | 'EVENT' | 'APPOINTMENT' | 'OUTLOOK';
  label: string;
}

export interface CalendarMeta {
  isLead: boolean;
  audiences: Record<CalendarAudience, string>;
  defaultReminderMinutes: Record<CalendarCategory, number>;
}

export interface CalendarOutlookState {
  /** Wie Termine in dein Outlook kommen: Firmen-Berechtigung, eigene Verbindung oder gar nicht */
  via: 'APP' | 'DELEGATED' | null;
  detail: string | null;
  app: { secretConfigured: boolean; permissionGranted: boolean };
  errors: { count: number; last: string | null };
  /** Lesen aus Outlook: zuletzt gelesen, letzter Fehler — fehlt bei älterem Server-Stand */
  read?: { syncedAt: string | null; lastError: string | null };
  delegatedConnected: boolean;
}

export interface CalendarSyncAdminStatus {
  secretConfigured: boolean;
  permissionGranted: boolean;
  users: Array<{ id: string; displayName: string; email: string; via: 'APP' | 'DELEGATED' | null; detail: string | null }>;
}

/** Für den Hinweis auf der Startseite der Admins — ohne Postfach-Probe */
export interface CalendarSyncSetupState {
  secretConfigured: boolean;
  permissionGranted: boolean;
  /** Microsoft-Anmeldung (App-Registrierung) vorhanden — die Einrichtung erweitert sie */
  microsoftConfigured: boolean;
  activeUsers: number;
  /** Mitarbeiter mit eigener Outlook-Verbindung: bei ihnen landen Termine auch ohne Firmen-Berechtigung */
  delegatedConnected: number;
}

export interface CalendarSyncSetupStart {
  sessionId: string;
  verificationUriComplete: string;
  verificationUri: string;
  userCode: string;
  expiresIn: number;
  pollIntervalMs: number;
}

export type CalendarSyncSetupPoll =
  | { status: 'pending'; pollIntervalMs: number }
  | { status: 'ok'; steps: string[]; permissionGranted: boolean }
  | { status: 'error'; message: string };

export function createCalendarApi(request: RequestFn) {
  const json = (body: unknown) => JSON.stringify(body);
  return {
    getCalendarFeed(from: string, to: string) {
      return request<CalendarFeed>(`/calendar/feed?from=${from}&to=${to}`);
    },
    getCalendarUpcoming() {
      return request<CalendarUpcoming>('/calendar/upcoming');
    },
    getCalendarMeta() {
      return request<CalendarMeta>('/calendar/meta');
    },
    getCalendarEvent(id: string) {
      return request<{ event: CalendarEvent }>(`/calendar/events/${encodeURIComponent(id)}`);
    },
    createCalendarEvent(input: CalendarEventInput) {
      return request<{ event: CalendarEvent }>('/calendar/events', { method: 'POST', body: json(input) });
    },
    updateCalendarEvent(id: string, input: CalendarEventInput) {
      return request<{ event: CalendarEvent }>(`/calendar/events/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: json({ ...input, scope: 'SERIES' }),
      });
    },
    /** Nur diesen Termin einer Serie verschieben */
    moveCalendarOccurrence(id: string, input: { occurrenceDate: string; start: CalendarWhen; end: CalendarWhen }) {
      return request<{ event: CalendarEvent }>(`/calendar/events/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: json({ ...input, scope: 'OCCURRENCE' }),
      });
    },
    cancelCalendarEvent(id: string, input: { scope: CalendarScope; occurrenceDate?: string; reason?: string | null }) {
      return request<{ event: CalendarEvent }>(`/calendar/events/${encodeURIComponent(id)}/cancel`, {
        method: 'POST',
        body: json(input),
      });
    },
    restoreCalendarEvent(id: string, input: { scope: CalendarScope; occurrenceDate?: string }) {
      return request<{ event: CalendarEvent }>(`/calendar/events/${encodeURIComponent(id)}/restore`, {
        method: 'POST',
        body: json(input),
      });
    },
    respondCalendarEvent(id: string, input: { response: CalendarResponse; note?: string | null }) {
      return request<{ event: CalendarEvent }>(`/calendar/events/${encodeURIComponent(id)}/respond`, {
        method: 'POST',
        body: json(input),
      });
    },
    getCalendarAvailability(start: string, end: string, excludeEventId?: string) {
      const qs = new URLSearchParams({ start, end });
      if (excludeEventId) qs.set('excludeEventId', excludeEventId);
      return request<{ busy: Record<string, CalendarBusyItem[]> }>(`/calendar/availability?${qs.toString()}`);
    },
    getCalendarOutlook() {
      return request<CalendarOutlookState>('/calendar/outlook');
    },
    getCalendarSyncStatus(probe = false) {
      return request<CalendarSyncAdminStatus>(`/admin/calendar-sync/status${probe ? '?probe=1' : ''}`);
    },
    getCalendarSyncSetupState() {
      return request<CalendarSyncSetupState>('/admin/calendar-sync/setup-state');
    },
    startCalendarSyncSetup() {
      return request<CalendarSyncSetupStart>('/admin/calendar-sync/auto-setup', { method: 'POST', body: '{}' });
    },
    pollCalendarSyncSetup(sessionId: string) {
      return request<CalendarSyncSetupPoll>(`/admin/calendar-sync/auto-setup/poll?sessionId=${encodeURIComponent(sessionId)}`);
    },
    runCalendarSync() {
      return request<{ result: Record<string, number | boolean> }>('/admin/calendar-sync/run', { method: 'POST', body: '{}' });
    },
  };
}
